import type { Material, Object3D } from 'three';
import type { SceneTransitionManager } from '../scene-transition-manager.class';
import type { MaterialState } from '../types';

/**
 * Check if an Object3D is a mesh-like object with materials.
 * Uses the `isMesh` property flag instead of `instanceof Mesh` because
 * CSG operations (three-csg-ts) may produce Mesh objects from a different
 * module instance of Three.js, causing `instanceof` to return false.
 */
const isMeshWithMaterial = (child: Object3D): child is Object3D & { material: Material | Array<Material> } =>
	!!(child as any).isMesh && !!(child as any).material;

const getMaterials = (child: Object3D & { material: Material | Array<Material> }): Array<Material> =>
	Array.isArray(child.material) ? child.material : [child.material];

/**
 * Prepare materials for fade animation by storing original state and enabling transparency
 *
 * @param self The SceneTransitionManager instance
 * @param object The object to prepare
 */
export const prepareMaterialsForFadeCfn = (
	self: SceneTransitionManager,
	object: Object3D
): void => {
	const materialStates = self.materialStates;

	object.traverse((child) => {
		if (isMeshWithMaterial(child)) {
			for (const material of getMaterials(child)) {
				if (!materialStates.has(material)) {
					materialStates.set(material, {
						transparent: material.transparent,
						opacity: material.opacity
					});
				}
				material.transparent = true;
			}
		}
	});
};

/**
 * Restore materials to their original state after fade animation
 *
 * @param self The SceneTransitionManager instance
 * @param object The object to restore
 */
export const restoreMaterialsCfn = (
	self: SceneTransitionManager,
	object: Object3D
): void => {
	const materialStates = self.materialStates;

	object.traverse((child) => {
		if (isMeshWithMaterial(child)) {
			for (const material of getMaterials(child)) {
				const state = materialStates.get(material);
				if (state) {
					material.transparent = state.transparent;
					material.opacity = state.opacity;
					materialStates.delete(material);
				}
			}
		}
	});
};

/**
 * Set opacity on all materials of an object.
 * Materials named 'Image-arch' are always kept at opacity 0 (transparent archway).
 *
 * @param object The object to set opacity on
 * @param opacity The opacity value (0-1)
 */
export const setObjectOpacityCfn = (
	object: Object3D,
	opacity: number
): void => {
	object.traverse((child) => {
		if (isMeshWithMaterial(child)) {
			for (const material of getMaterials(child)) {
				if (material.name === 'Image-arch') {
					material.opacity = 0;
				} else {
					material.opacity = opacity;
				}
			}
		}
	});
};
